import React, { useEffect, useState } from "react";
import { get, deleteRequest } from "api";
import DataTable from "react-data-table-component";
import CircularProgress from "@mui/material/CircularProgress";
import { useHistory } from "react-router-dom";
import RefreshIcon from "@mui/icons-material/Refresh";
import { IListLesson } from "types/addLesson";

const ListLesson: React.FC = () => {
  const history = useHistory();
  const [lessons, setLessons] = useState<IListLesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    get("/lessons")
      .then((response) => {
        setLessons(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching lesson data:", error);
        setLoading(false);
      });
  }, []);

  const handleEditClick = (slug: string) => {
    history.push(`/dashboard/edit-lesson/${slug}`);
  };

  const handleDeleteClick = (id: string) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this lesson?"
    );
    if (confirmDelete) {
      deleteRequest(`/lessons/${id}`)
        .then(() => {
          return get("/lessons");
        })
        .then((response) => {
          setLessons(response.data);
        })
        .catch((error) => {
          console.error("Error deleting lesson:", error);
        });
    }
  };

  const handleRowClicked = (row: IListLesson) => {
    history.push(`/dashboard/edit-lesson/${row.slug}`);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const keyword = e.target.value.toLowerCase();
    setSearchQuery(keyword);
  };

  const columns = [
    {
      name: "Title",
      selector: (row: IListLesson) => row.title,
      sortable: true,
      grow: 1,
    },
    {
      name: "Description",
      selector: (row: IListLesson) => row.description,
      sortable: true,
    },
    {
      name: "Action",
      cell: (row: IListLesson) => (
        <div className="btn-group" role="group" aria-label="Basic example">
          <button
            type="button"
            onClick={() => handleEditClick(row.slug)}
            className="btn btn-warning me-2"
          >
            Edit
          </button>

          <button
            type="button"
            onClick={() => handleDeleteClick(row._id)}
            className="btn btn-danger"
          >
            Delete
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
    },
  ];

  const customStyles = {
    headRow: {
      style: {
        minWidth: "100%",
      },
    },
    headCells: {
      style: {
        paddingLeft: "8px",
        paddingRight: "8px",
      },
    },
    cells: {
      style: {
        paddingLeft: "8px",
        paddingRight: "8px",
      },
    },
    search: {
      style: {
        position: "relative",
        width: "100%",
      },
    },
    searchInput: {
      style: {
        width: "100%",
      },
    },
  };

  const subHeaderComponentMemo = React.useMemo(() => {
    return (
      <div className="d-flex align-items-center w-100">
        <input
          type="text"
          placeholder="Search lesson..."
          onChange={handleSearch}
          className="form-control me-2"
          value={searchQuery}
        />
        <div className="cursor-pointer" onClick={() => setSearchQuery("")}>
          <RefreshIcon />
        </div>
      </div>
    );
  }, [searchQuery]);

  const filteredLessons = lessons.filter(
    (lesson) =>
      lesson.title.toLowerCase().includes(searchQuery) ||
      lesson.description.toLowerCase().includes(searchQuery)
  );

  return (
    <div className="">
      <div className="w-100">
        <h3>Lesson List</h3>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress size={30} />
          </div>
        ) : (
          <DataTable
            subHeader
            subHeaderComponent={subHeaderComponentMemo}
            data={filteredLessons}
            columns={columns}
            pagination
            paginationPerPage={20}
            customStyles={customStyles}
            onRowClicked={handleRowClicked}
            highlightOnHover
            pointerOnHover
            paginationComponentOptions={{
              noRowsPerPage: true,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ListLesson;
